import { getTextNodesInElement } from "lib/text/char-position";
import { stripSoftHyphens } from "lib/text/text-content";
import { findXpointerAnchor } from "lib/text/xpointer-range";

// Граница слова — пробелы и знаки пунктуации/кавычки, через которые
// не нужно расширять выделение
function isWordBoundary(char: string): boolean {
  return /[\s\p{P}\p{S}]/u.test(char);
}

interface TextNodeMapping {
  node: Text;
  start: number;
}

function buildTextNodeMap(anchor: Element): {
  concat: string;
  map: TextNodeMapping[];
} {
  const textNodes = getTextNodesInElement(anchor);
  let concat = "";
  const map: TextNodeMapping[] = [];
  for (const node of textNodes) {
    map.push({ node, start: concat.length });
    concat += node.textContent || "";
  }
  return { concat, map };
}

function concatOffsetToNodePosition(
  map: TextNodeMapping[],
  offset: number
): { node: Text; offset: number } | null {
  for (let i = map.length - 1; i >= 0; i--) {
    if (offset >= map[i].start) {
      return { node: map[i].node, offset: offset - map[i].start };
    }
  }
  return map.length > 0 ? { node: map[0].node, offset: 0 } : null;
}

function nodeOffsetInMap(
  map: TextNodeMapping[],
  node: Node,
  offset: number
): number | null {
  const entry = map.find(m => m.node === node);
  if (!entry) return null;
  return entry.start + offset;
}

/**
 * Расширяет Range до границ слов, обходя элементные границы через
 * конкатенацию всех текстовых узлов внутри xpointer-якоря.
 * Не трогает Selection API — безопасно для iOS.
 */
export function expandRangeToWordBoundaries(range: Range): {
  expandedRange: Range;
  text: string;
} | null {
  const startAnchor = findXpointerAnchor(range.startContainer);
  const endAnchor = findXpointerAnchor(range.endContainer);
  if (!startAnchor || !endAnchor) return null;

  const sameAnchor = startAnchor === endAnchor;

  const startMap = buildTextNodeMap(startAnchor);
  const endMap = sameAnchor ? startMap : buildTextNodeMap(endAnchor);

  const concatStart = nodeOffsetInMap(
    startMap.map,
    range.startContainer as Text,
    range.startOffset
  );
  const concatEnd = nodeOffsetInMap(
    endMap.map,
    range.endContainer as Text,
    range.endOffset
  );
  if (concatStart === null || concatEnd === null) return null;

  let expandedStart = concatStart;
  while (
    expandedStart > 0 &&
    !isWordBoundary(startMap.concat[expandedStart - 1])
  ) {
    expandedStart--;
  }

  let expandedEnd = concatEnd;
  while (
    expandedEnd < endMap.concat.length &&
    !isWordBoundary(endMap.concat[expandedEnd])
  ) {
    expandedEnd++;
  }

  const startPos = concatOffsetToNodePosition(startMap.map, expandedStart);
  const endPos = concatOffsetToNodePosition(endMap.map, expandedEnd);
  if (!startPos || !endPos) return null;

  const expandedRange = document.createRange();
  expandedRange.setStart(startPos.node, startPos.offset);
  expandedRange.setEnd(endPos.node, endPos.offset);

  let text: string;
  if (sameAnchor) {
    text = startMap.concat.slice(expandedStart, expandedEnd);
  } else {
    const startPart = startMap.concat.slice(expandedStart);
    const endPart = endMap.concat.slice(0, expandedEnd);
    const middleText = range.cloneContents().textContent || "";
    text = startPart + middleText + endPart;
  }

  return { expandedRange, text: stripSoftHyphens(text) };
}

/**
 * Десктоп: расширяет нативное выделение через Selection API.
 * НЕ вызывать на iOS — ломает ручки выделения.
 */
export function expandSelectionToWordBoundaries() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const range = sel.getRangeAt(0);
  const result = expandRangeToWordBoundaries(range);
  if (!result) return;

  const { expandedRange } = result;
  const changed =
    expandedRange.startContainer !== range.startContainer ||
    expandedRange.startOffset !== range.startOffset ||
    expandedRange.endContainer !== range.endContainer ||
    expandedRange.endOffset !== range.endOffset;

  if (changed) {
    sel.removeAllRanges();
    sel.addRange(expandedRange);
  }
}
